import React from 'react';
import PropTypes from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';

import CalendarDayWithContent from './CalendarDayWithContent';
import Badge from '../Badge';

/**
 * These bitty components render the visual appearance of rec.gov 's styled
 * available/walkup/lottery/blocked days
 */
export const WalkupCalendarDay = ({ day }) => (
    <div className="rec-calendar-day rec-walkup-day">
        <div>{day.format('D')}</div>
        <div><strong>W</strong></div>
    </div>
);

export const FFCalendarDay = ({ day }) => (
    <div className="rec-calendar-day rec-walkup-day">
        <div>{day.format('D')}</div>
        <div><strong>FF</strong></div>
    </div>
);

export const BlockedCalendarDay = ({ day }) => (
    <div className="rec-calendar-day rec-blocked-day">
        <div>{day.format('D')}</div>
    </div>
);

export const LotteryCalendarDay = ({ day }) => (
    <div className="rec-calendar-day rec-lottery-day">
        <div>{day.format('D')}</div>
        <div><strong>L</strong></div>
    </div>
);

export const AvailableCalendarDay = ({ day }) => (
    <div className="rec-calendar-day rec-available-day">
        <div>{day.format('D')}</div>
        <div><strong>A</strong></div>
    </div>
);

export const CurrentReservationCalendarDay = ({ day }) => (
    <div className="rec-calendar-day rec-available-day">
        <div>{day.format('D')}</div>
        <div><strong>R</strong></div>
    </div>
);

export const QuantityCalendarDay = ({ day, quantity }) => (
    <div className="rec-calendar-day rec-available-day rec-quantity-day">
        <div>{day.format('D')}</div>
        <div><Badge>{quantity}</Badge></div>
    </div>
);


/**
 * renderDayContents is used by the *WithKey components to render the visual
 * appearance of rec.gov 's available/walkup/lottery/blocked days. This content
 * is rendered inside of the <td role="button"> and is inaccessible to SRs.
 *
 * renderDayContents should be called with getDayType, a required prop for
 * the *WithKey components. It returns one of these strings: 'walkup',
 * 'lottery', 'blocked', 'currentReservation', available'
 */
export const renderDayWithTypeContents = (getDayType) => (day) => {
    const dayType = getDayType(day);

    switch (dayType) {
        case 'walkup':
            return <FFCalendarDay day={day} />
        case 'old-walkup':
            return <WalkupCalendarDay day={day} />
        case 'lottery':
            return <LotteryCalendarDay day={day} />
        case 'blocked':
            return <BlockedCalendarDay day={day} />
        case 'currentReservation':
            return <CurrentReservationCalendarDay day={day} />
        default:
            return <AvailableCalendarDay day={day} />
    }
}

export const renderDayWithQuantity = (getQuantity) => (day) => {
    const quantity = getQuantity(day);

    return <QuantityCalendarDay day={day} quantity={quantity} />
}


/**
 * renderCalendarDay is a react-dates prop that, when passed, replaces the
 * internal implementation of the CalendarDay component.
 *
 * We use it here to render the <td role="button"> with aria-labels appropriate
 * for the available/walkup/lottery/blocked day
 *
 * renderCalendarDay should be called with getDayType, a required prop for
 * the *WithKey components. It returns one of these strings: 'walkup',
 * 'lottery', 'blocked', 'available'
 */
export const renderCalendarDayWithType = (getDayType, getTooltipContent) => (props) => {
    const hasTooltip = !!getTooltipContent;

    return (
        <CalendarDayWithContent
            {...props}
            getDayType={getDayType}
            hasTooltip={hasTooltip}
            getTooltipContent={getTooltipContent} />
    );
}

/**
 * Similar to renderCalendarDayWithType, but renders a quantity in the cell.
 *
 * getQuantity should return a number
 */
export const renderCalendarDayWithQuantity = (getQuantity, getTooltipContent) => (props) => {
    const hasTooltip = !!getTooltipContent;

    return (
        <CalendarDayWithContent
            {...props}
            getQuantity={getQuantity}
            hasTooltip={hasTooltip}
            getTooltipContent={getTooltipContent} />
    );
}

const dayPropTypes = {
    day: momentPropTypes.momentObj
};

WalkupCalendarDay.propTypes = dayPropTypes;
FFCalendarDay.propTypes = dayPropTypes;
BlockedCalendarDay.propTypes = dayPropTypes;
LotteryCalendarDay.propTypes = dayPropTypes;
AvailableCalendarDay.propTypes = dayPropTypes;
QuantityCalendarDay.propTypes = {
    day: momentPropTypes.momentObj,
    quantity: PropTypes.number
};
CurrentReservationCalendarDay.propTypes = dayPropTypes;